import React from 'react';

// Components
import { CreateModal, DeleteModal, EditModal } from './components/CustomScenarioModals';
import HistoricalScenariosFilterDropdown from './components/HistoricalScenariosFilterDropdown';
import HistoricalScenariosFilterOptions from './components/HistoricalScenariosFilterOptions';
import { SearchInput } from '@/views/components/inputs';

// Hooks
import { useHistoricalScenariosFilter } from './hooks/useHistoricalScenariosFilter';
import { useHistoricalScenariosModals } from './hooks/useHistoricalScenariosModals';

// Types
import Nullable from '@/types/nullable';
import User from '@/types/user';

// Utils
import { formatMessage } from '@/utils/i18n';

interface HistoricalScenarioFilterProps {
  user?: Nullable<User>;
  readOnly?: boolean;
}

const HistoricalScenariosFilter: React.FC<HistoricalScenarioFilterProps> = ({ user, readOnly }) => {
  const {
    containerRef,
    customScenarios,
    filterButtonRef,
    handleEscapeKey,
    handleOnToggleFilter,
    isOpen,
    onScenarioChange,
    resetFilter,
    predefinedScenarios,
    selectedHistoricalScenarios,
    search,
    setSearch,
  } = useHistoricalScenariosFilter();

  const {
    customScenarioToUpdate,
    handleOnCreateScenario,
    handleOnDeleteScenario,
    handleOnEditScenario,
    isLoadingCreate,
    isLoadingDelete,
    isLoadingUpdate,
    onCloseModal,
    onConfirmDeleteScenario,
    onSubmitCreateScenario,
    onSubmitEditScenario,
    showCreateModal,
    showDeleteModal,
    showEditModal,
  } = useHistoricalScenariosModals({ resetFilter });

  return (
    <div className="historical-scenarios-filter filter filter-v2" data-dd-privacy="mask" data-private ref={containerRef}>
      <HistoricalScenariosFilterDropdown
        buttonRef={filterButtonRef}
        isOpen={isOpen}
        label={formatMessage('Search.Scenarios.SelectScenarios')}
        onClick={handleOnToggleFilter}
        totalSelected={selectedHistoricalScenarios?.length ?? 0}
      />

      {isOpen && (
        <div className="filter-dropdown dropdown" onKeyDown={handleEscapeKey}>
          <SearchInput leftSection={null} onChange={setSearch} placeholder={formatMessage('Common.Search')} value={search} />
          <HistoricalScenariosFilterOptions
            customScenarios={customScenarios}
            onScenarioChange={onScenarioChange}
            onCreateScenario={handleOnCreateScenario}
            onDeleteScenario={handleOnDeleteScenario}
            onEditScenario={handleOnEditScenario}
            predefinedScenarios={predefinedScenarios}
            search={search}
            selectedScenarios={selectedHistoricalScenarios}
            user={user}
            readOnly={readOnly}
          />
        </div>
      )}

      {showCreateModal && <CreateModal onClose={onCloseModal} onSubmit={onSubmitCreateScenario} show={showCreateModal} isLoading={isLoadingCreate} />}
      {showDeleteModal && (
        <DeleteModal
          customScenario={customScenarioToUpdate!}
          onClose={onCloseModal}
          onConfirm={onConfirmDeleteScenario}
          show={showDeleteModal}
          isLoading={isLoadingDelete}
        />
      )}
      {showEditModal && (
        <EditModal
          customScenario={customScenarioToUpdate!}
          onClose={onCloseModal}
          onSubmit={onSubmitEditScenario}
          show={showEditModal}
          isLoading={isLoadingUpdate}
        />
      )}
    </div>
  );
};

export default HistoricalScenariosFilter;
